import React, {PropsWithChildren} from 'react';
import BrowserUtil from '../util/BrowserUtil';
import NextFrame from '../util/NextFrame';
import './UITable.scss';

export interface ITableColumn{
    width:number;
    flexGrow?:number;
    dataKey:string;
    name:string | JSX.Element;
    dataGetter?:($data:any)=>any;
    classGetter?:($data:any)=>string;
    headerRenderer?:ITableHeaderRenderer;
    onMouseOver?:($data:any,$column:ITableColumn)=>void;
    onMouseOut?:($data:any,$column:ITableColumn)=>void;
    onClick?:($data:any,$column:ITableColumn)=>void;
}

export interface IUITableProps extends PropsWithChildren {
    sortEnabled:boolean;
    columns:ITableColumn[];
    data:any[];
    rowRenderer:ITableRowRenderer;
    headerH?:number;
    rowH?:number;
    extraClassName?:string;
    hideHeader?:boolean;
    fixedHeaderOffset?:number;
    useContainer?:boolean;
    contentChildren?:JSX.Element;
    onScroll?:($tableDiv:HTMLDivElement)=>void;
    onHeaderScroll?:($tableHeader:HTMLDivElement)=>void;
    reflectContentHeightToCSS?:boolean;
    onMouseMove?:($x:number,$y:number)=>void;
    initSortBy?:string;
    sort:($sortBy:string, $direction:ITableSortDirectionType, $a:any, $b:any)=>number;
    initSortByDirection?:ITableSortDirectionType;
    
    


}

export interface IUITableState{
    
    contentHeight?:number;
    isTableContentScrollbarShowing?:boolean;
    sortBy:string;
    sortDirection:string;
    sortEnabled:boolean;
}

export type ITableHeaderRenderer  = ($data:any, $index:number)=>JSX.Element;
export type ITableRowRenderer  = ($data:any, $columns:JSX.Element, $className:string, $key:string, $index:number)=>JSX.Element;
export type ITableSortDirectionType = string;

export default class UITable extends React.Component<IUITableProps, IUITableState> {

    table:React.RefObject<HTMLDivElement> = React.createRef();
    tableHeader:React.RefObject<HTMLDivElement> = React.createRef();
    tableContent:React.RefObject<HTMLDivElement> = React.createRef();

    static SORT_DIR_ASC:ITableSortDirectionType = "ASC";
    static SORT_DIR_DESC:ITableSortDirectionType = "DESC";

    static BASIC_SORT = ($direction:string, $stringA:string | number, $stringB:string | number)=>{

        let a:string = String($stringA).toLowerCase();
        let b:string = String($stringB).toLowerCase();

        if(a===b){
            return 0;
        }else{
            if(a > b){
                return $direction===UITable.SORT_DIR_ASC?1:-1;
            }else{
                return $direction===UITable.SORT_DIR_ASC?-1:1;
            }
        }

    }

    static NUM_SORT = ($direction:string, $numA:number, $numB:number)=>{

        let a:number = $numA;
        let b:number = $numB;

        if(a===b){
            return 0;
        }else{
            if(a > b){
                return $direction===UITable.SORT_DIR_ASC?1:-1;
            }else{
                return $direction===UITable.SORT_DIR_ASC?-1:1;
            }
        }

    }

    static NUM_SORT_REVERSE = ($direction:string, $numA:number, $numB:number)=>{

        let a:number = $numA;
        let b:number = $numB;

        if(a===b){
            return 0;
        }else{
            if(a > b){
                return $direction===UITable.SORT_DIR_ASC?-1:1;
            }else{
                return $direction===UITable.SORT_DIR_ASC?1:-1;
            }
        }

    }


    constructor($props:IUITableProps){
        super($props);

        let sortBy = this.props.initSortBy;

        let sortDirection:ITableSortDirectionType = UITable.SORT_DIR_ASC;
        if(this.props.initSortByDirection){
            sortDirection = this.props.initSortByDirection;
        }

        this.state = {
            sortBy:sortBy||"",
            sortDirection:sortDirection,
            sortEnabled:this.props.sortEnabled,
        }
    }

    componentDidMount(){
        this._updateContentHeight();
        this._checkForTableContentScrollbar();
        if(this.props.reflectContentHeightToCSS){
            window.addEventListener("resize", this._onWindowResize);
        }
    }
    componentDidUpdate(){
        this._updateContentHeight();
        this._checkForTableContentScrollbar();
    }
    componentWillUnmount(){
        

        if(this.props.reflectContentHeightToCSS){
            window.removeEventListener("resize", this._onWindowResize);
        }
    }
    _onWindowResize=()=>{
        NextFrame(()=>{
            this._updateContentHeight(false);
        });
    }
    _checkForTableContentScrollbar=()=>{
        
        if(this.tableContent.current){

            let isTableContentScrollbarShowing:boolean=false;
            if(this.tableContent.current.scrollHeight>this.tableContent.current.clientHeight){
                isTableContentScrollbarShowing=true;
            }

            if(this.state.isTableContentScrollbarShowing!==isTableContentScrollbarShowing){
                NextFrame(()=>{
                    this.setState({isTableContentScrollbarShowing:isTableContentScrollbarShowing});
                });
            }
        }
    }

    _updateContentHeight=($nextFrame:boolean=true)=>{


        if(this.tableContent.current){

            if(this.props.reflectContentHeightToCSS){
                let contentHeight = 0;
                
                for(let i:number=0; i<this.tableContent.current.children.length; i++){
                    contentHeight += this.tableContent.current.children[i].clientHeight
                }
    
                if(this.state.contentHeight!==contentHeight){
                    if($nextFrame){
    
                        NextFrame(()=>{
                            this.setState({contentHeight});
                        });
                    }else{
                        this.setState({contentHeight});
                    }
                }
            }

        }

    }

    _generateRowColumns=($data:any)=>{
        return (
            <>
                {this.props.columns.map(($column,$columnIndex)=>{
                    let data = $data[$column.dataKey];
                    return (
                        <UITableRowColumn key={$column.dataKey+"tableRowColumn"+$columnIndex} data={data} rowData={$data} dataColumn={$column}/>
                    );
                })}
            </>
        )
    }
    _onScroll=()=>{
        if(this.props.onScroll && this.table.current){
            this.props.onScroll(this.table.current);
        }
    }
    _onHeaderScroll=()=>{
        if(this.props.onHeaderScroll && this.tableHeader.current){
            this.props.onHeaderScroll(this.tableHeader.current);
        }
    }

    _onSort=($info:{sortBy:string, sortDirection:ITableSortDirectionType})=>{
        if(!$info.sortBy) {
            return
        }
        this.setState({sortBy:$info.sortBy, sortDirection:$info.sortDirection});
    }


    render() {
        let strCN:string = "table";
        if(this.props.extraClassName){
            strCN+=" "+this.props.extraClassName;
        }

        let numHeaderH:number = this.props.headerH || 50;
        

        let showHeader:boolean=true;
        if(this.props.hideHeader){
            showHeader=false;
        }

        let headerStyle:React.CSSProperties = {};
        if(this.props.fixedHeaderOffset){
            headerStyle.position="absolute";
            headerStyle.top = this.props.fixedHeaderOffset+"px";
        }

        let contentStyle:React.CSSProperties = {};

        if(this.props.reflectContentHeightToCSS){
            if(this.state.contentHeight){
                contentStyle.height=this.state.contentHeight+"px";
            }
        }
        if(this.state.isTableContentScrollbarShowing){
            headerStyle.paddingRight=BrowserUtil.getScrollBarWidth();
        }

        let data:any[] = this.props.data;

        data.sort(($a:any,$b:any)=>{

            let sortResult = this.props.sort(this.state.sortBy,this.state.sortDirection,$a,$b);
            return sortResult;
        });

        let jsxContent:JSX.Element = (
            <>
                {showHeader && (
                    <div onScroll={this._onHeaderScroll} ref={this.tableHeader} className="tableHeader" style={{height:numHeaderH+"px", ...headerStyle}}>
                        {this.props.columns.map(($column,$index)=>{
                            return (
                                <UITableColumn styles={{height:numHeaderH+"px"}} key={$index+$column.dataKey} data={$column} index={$index}
                                 onSort={this._onSort} direction={this.state.sortDirection} sortEnabled={this.state.sortEnabled}/>
                            )
                        })}
                    </div>
                )}
                <div className="tableContent" ref={this.tableContent} style={contentStyle}>
                    {data.map(($data:any, $index)=>{
                        return this.props.rowRenderer($data, this._generateRowColumns($data), "tableRow", "tableRow"+$index, $index);
                    })}                    
                    {this.props.contentChildren}
                </div>
                {this.props.children}
            </>
        );

        return (
            <div className={strCN} ref={this.table} onScroll={this._onScroll}>
                {this.props.useContainer && (
                    <div className="tableContainer">
                        {jsxContent}
                    </div>
                )}
                {!this.props.useContainer && jsxContent}
            </div>
        );
    }
}

interface IUITableColumnProps {
    data:ITableColumn;
    styles:React.CSSProperties;
    index:number;
    onSort:($info:{sortBy:string, sortDirection:ITableSortDirectionType})=>void;
    direction:string;
    sortEnabled:boolean;
}

class UITableColumn extends React.Component<IUITableColumnProps> {

    _onClick=()=>{

        if (!this.props.sortEnabled){
           return;
        }

        let direction = this.props.direction == "ASC"? "DESC" : "ASC";
        this.props.onSort({
            sortBy: this.props.data.dataKey,
            sortDirection: direction
        })

        
    }

    render() {


        let strCN:string = "tableColumn dataKey_"+this.props.data.dataKey;

        let content:JSX.Element = <>{this.props.data.name}{"\u00a0"} </>
        if(this.props.data.headerRenderer){
            content = this.props.data.headerRenderer(this.props.data, this.props.index);
        }

        return (
            <>
            <div 
                onClick={this._onClick}
                className={strCN} 
                style={{width:this.props.data.width+"px", flexGrow:this.props.data.flexGrow, ...this.props.styles}}>
                {content}
                
                

            </div>
            
            </>
        );
    }
}



interface IUITableRowColumnProps {
    data:any;
    rowData:any;
    dataColumn:ITableColumn;
}

class UITableRowColumn extends React.Component<IUITableRowColumnProps> {
    _onClick=()=>{
        if(this.props.dataColumn.onClick) this.props.dataColumn.onClick(this.props.rowData, this.props.dataColumn);
    }
    _onMouseOver=()=>{
        if(this.props.dataColumn.onMouseOver) this.props.dataColumn.onMouseOver(this.props.rowData, this.props.dataColumn);
    }
    _onMouseOut=()=>{
        if(this.props.dataColumn.onMouseOut) this.props.dataColumn.onMouseOut(this.props.rowData, this.props.dataColumn);
    }
    render() {
        let strCN:string = "tableRowColumn dataKey_"+this.props.dataColumn.dataKey;


        let rowValue = this.props.data;
        if(this.props.dataColumn.dataGetter){
            rowValue = this.props.dataColumn.dataGetter(this.props.rowData);
        }
        if(this.props.dataColumn.classGetter){
            strCN+=" "+this.props.dataColumn.classGetter(this.props.rowData);
        }

        let extraProps:any = {};
        if(this.props.dataColumn.onClick){
            extraProps.onClick=this._onClick;
        }
        if(this.props.dataColumn.onMouseOut){
            extraProps.onMouseOut=this._onMouseOut;
        }
        if(this.props.dataColumn.onMouseOver){
            extraProps.onMouseOver=this._onMouseOver;
        }
        return (
            <div
                onClick={this._onClick} 
                {...extraProps}
                className={strCN} 
                style={{width:this.props.dataColumn.width+"px", flexGrow:this.props.dataColumn.flexGrow}}>
                {rowValue}
            </div>
        );
    }
}
